/**
 * Tells the reader why an email link did not open its organization (#1232).
 *
 * Renders nothing; lives inside `ToastContextProvider`, which the root layout
 * mounts BELOW `OrganizationProvider` — so the context only raises the flag,
 * and this leaf turns it into a toast once.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useToast } from '@/contexts/ToastContext';

const NOTICE_DURATION_MS = 10_000;

export function OrgLinkNotice(): null {
  const { linkOrgDenied, dismissLinkOrgNotice } = useOrganization();
  const { toast } = useToast();
  const { t } = useTranslation('common');
  // StrictMode re-runs the effect before the dismissal lands; one link, one toast.
  const shown = useRef(false);

  useEffect(() => {
    if (!linkOrgDenied || shown.current) return;
    shown.current = true;
    toast.info(
      t('common.orgLinkNoAccessTitle'),
      t(
        linkOrgDenied === 'no-organizations'
          ? 'common.orgLinkNoOrganizationsDescription'
          : 'common.orgLinkNoAccessDescription'
      ),
      NOTICE_DURATION_MS
    );
    dismissLinkOrgNotice();
  }, [linkOrgDenied, dismissLinkOrgNotice, toast, t]);

  return null;
}
